/**
 * ADF Monitor Page
 *
 * Azure Data Factory pipeline monitoring dashboard
 * Shows pipeline runs, errors, and skipped rows
 */

import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, AlertCircle, CheckCircle2, Clock, XCircle } from "lucide-react";
import { PipelineRunsTable } from "@/components/adf/PipelineRunsTable";
import { FailedTablesView } from "@/components/adf/FailedTablesView";
import { SkippedRowsViewer } from "@/components/adf/SkippedRowsViewer";
import { ErrorSummaryChart } from "@/components/adf/ErrorSummaryChart";
import { buildApiUrl } from "@/lib/api";
import { ViewGuard } from "@/components/auth/PermissionGuards";
import { MonitoringLayout } from "@/pages/monitoring/layout";

interface PipelineRunsSummary {
  total: number;
  succeeded: number;
  failed: number;
  inProgress: number;
  cancelled: number;
  queued: number;
}

interface PipelineRunsResponse {
  summary: PipelineRunsSummary;
  runs: any[];
}

interface ErrorSummaryResponse {
  summary: any[];
  totalErrors: number;
  uniqueTables: number;
}

interface SkippedRowsResponse {
  total: number;
  tables: number;
  byTable: Record<string, any[]>;
  all: any[];
}

export default function ADFMonitorPage() {
  const [hoursFilter, setHoursFilter] = useState(24);
  const [activeTab, setActiveTab] = useState("overview");

  // Load showAll state from localStorage (default: false)
  const [showAll, setShowAll] = useState(() => {
    const saved = localStorage.getItem('adf-monitor-show-all');
    return saved === 'true';
  });

  // Save showAll state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('adf-monitor-show-all', String(showAll));
  }, [showAll]);

  // Handle hash-based navigation to tabs
  useEffect(() => {
    const hash = window.location.hash.slice(1); // Remove # from hash
    if (hash && ['overview', 'failed', 'skipped', 'errors'].includes(hash)) {
      setActiveTab(hash);
    }
  }, []);

  // Check if we're viewing child runs for a specific parent
  const urlParams = new URLSearchParams(window.location.search);
  const parentRunId = urlParams.get('parentRun');

  // Fetch pipeline runs (or child runs if parentRunId is provided)
  const {
    data: pipelineData,
    isLoading: isLoadingRuns,
    error: runsError,
    refetch: refetchRuns,
  } = useQuery<PipelineRunsResponse>({
    queryKey: parentRunId ? ['adf-child-runs', parentRunId, hoursFilter, showAll] : ['adf-pipeline-runs', hoursFilter, showAll],
    queryFn: async () => {
      if (parentRunId) {
        // Fetch child runs for specific parent
        const response = await fetch(
          buildApiUrl(`/api/adf/runs/${parentRunId}/children?hours=${hoursFilter}`)
        );
        if (!response.ok) {
          throw new Error('Failed to fetch child runs');
        }
        const data = await response.json();

        // Transform to match PipelineRunsResponse format
        return {
          summary: data.summary,
          runs: data.childRuns
        };
      } else {
        // Fetch parent runs
        const response = await fetch(
          buildApiUrl(`/api/adf/pipeline-runs?hours=${hoursFilter}&enrich=true&showAll=${showAll}`)
        );
        if (!response.ok) {
          throw new Error('Failed to fetch pipeline runs');
        }
        return response.json();
      }
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch error summary
  const {
    data: errorData,
    isLoading: isLoadingErrors,
  } = useQuery<ErrorSummaryResponse>({
    queryKey: ['adf-error-summary', hoursFilter, showAll],
    queryFn: async () => {
      const response = await fetch(
        buildApiUrl(`/api/adf/error-summary?hours=${hoursFilter}&showAll=${showAll}`)
      );
      if (!response.ok) {
        throw new Error('Failed to fetch error summary');
      }
      return response.json();
    },
    refetchInterval: 60000, // Refresh every minute
  });

  // Fetch skipped rows
  const {
    data: skippedData,
    isLoading: isLoadingSkipped,
  } = useQuery<SkippedRowsResponse>({
    queryKey: ['adf-skipped-rows', hoursFilter, showAll],
    queryFn: async () => {
      const response = await fetch(
        buildApiUrl(`/api/adf/skipped-rows?hours=${hoursFilter}&showAll=${showAll}`)
      );
      if (!response.ok) {
        throw new Error('Failed to fetch skipped rows');
      }
      return response.json();
    },
    refetchInterval: 60000,
  });

  const summary = pipelineData?.summary || {
    total: 0,
    succeeded: 0,
    failed: 0,
    inProgress: 0,
    cancelled: 0,
    queued: 0,
  };

  const successRate = summary.total > 0
    ? ((summary.succeeded / summary.total) * 100).toFixed(1)
    : '0';

  // Calculate unique failed tables from pipeline runs
  const uniqueFailedTables = useMemo(() => {
    if (!pipelineData?.runs) return 0;

    const tableNames = new Set<string>();
    pipelineData.runs
      .filter(r => r.status === 'Failed')
      .forEach(run => {
        if (run.failedActivities) {
          run.failedActivities.forEach((activity: any) => {
            const tableName = activity.tableName || run.lastTableProcessed || run.parameters?.TableName;
            if (tableName && tableName !== 'Unknown') {
              tableNames.add(tableName);
            }
          });
        }
      });

    return tableNames.size;
  }, [pipelineData]);

  const handleRefresh = () => {
    refetchRuns();
  };

  return (
    <ViewGuard section="scheduler" fallback={
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <div className="mb-4">
            <span className="material-icons text-6xl text-gray-400">block</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to view ADF Monitor</p>
        </div>
      </div>
    }>
    <MonitoringLayout>
    <div className="w-full p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {parentRunId ? 'Child Pipeline Runs' : 'Data Ingestion Monitor'}
          </h1>
          <p className="text-muted-foreground">
            {parentRunId ? (
              <>
                Viewing child runs for parent: <code className="text-xs bg-muted px-1 py-0.5 rounded">{parentRunId}</code>
              </>
            ) : (
              'Azure Data Factory pipeline status and error tracking'
            )}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {parentRunId && (
            <Button onClick={() => window.close()} variant="outline" size="sm">
              Close
            </Button>
          )}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              className="w-4 h-4 cursor-pointer"
            />
            <span className="whitespace-nowrap">Show all pipelines</span>
          </label>
          <select
            value={hoursFilter}
            onChange={(e) => setHoursFilter(Number(e.target.value))}
            className="px-3 py-2 border rounded-md"
          >
            <option value={6}>Last 6 hours</option>
            <option value={12}>Last 12 hours</option>
            <option value={24}>Last 24 hours</option>
            <option value={48}>Last 48 hours</option>
            <option value={168}>Last 7 days</option>
          </select>
          <Button onClick={handleRefresh} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {runsError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load pipeline data: {runsError instanceof Error ? runsError.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Runs</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.total}</div>
            <p className="text-xs text-muted-foreground">
              Last {hoursFilter} hours
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{successRate}%</div>
            <p className="text-xs text-muted-foreground">
              {summary.succeeded} successful
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{summary.failed}</div>
            <p className="text-xs text-muted-foreground">
              {summary.failed} pipeline run{summary.failed !== 1 ? 's' : ''}, {uniqueFailedTables} unique table{uniqueFailedTables !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{summary.inProgress}</div>
            <p className="text-xs text-muted-foreground">
              Currently running
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Skipped Rows</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{skippedData?.total || 0}</div>
            <p className="text-xs text-muted-foreground">
              {skippedData?.tables || 0} tables affected
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="failed">
            Failed Tables
            {summary.failed > 0 && (
              <Badge variant="destructive" className="ml-2">
                {summary.failed}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="skipped">
            Skipped Rows
            {(skippedData?.total || 0) > 0 && (
              <Badge variant="secondary" className="ml-2">
                {skippedData?.total}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="errors">Error Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Pipeline Runs</CardTitle>
              <CardDescription>
                All pipeline executions in the last {hoursFilter} hours
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PipelineRunsTable
                runs={pipelineData?.runs || []}
                isLoading={isLoadingRuns}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="failed" className="space-y-4">
          <FailedTablesView
            runs={pipelineData?.runs || []}
            isLoading={isLoadingRuns}
          />
        </TabsContent>

        <TabsContent value="skipped" className="space-y-4">
          <SkippedRowsViewer
            data={skippedData}
            isLoading={isLoadingSkipped}
          />
        </TabsContent>

        <TabsContent value="errors" className="space-y-4">
          <ErrorSummaryChart
            data={errorData}
            isLoading={isLoadingErrors}
          />
        </TabsContent>
      </Tabs>
    </div>
    </MonitoringLayout>
    </ViewGuard>
  );
}
